import { AxiosInstance } from 'axios';

import {
  AuthenticationToken,
  AuthServiceFactory,
  generateConf,
  setAuth,
  UserServiceFactory,
} from '@topconagriculture/frontend-utils';

import CodeServiceFactory from './code/code.api';
import CommonServiceFactory from './common/common.api';
import CompanyServiceFactory from './company/company.api';
import FarmsServiceFactory from './farms/farms.api';
import FeedingServiceFactory from './feeding/feeding.api';
import GroupServiceFactory from './group/group.api';
import GroupTypesServiceFactory from './groupType/groupType.api';
import IngredientServiceFactory from './ingredients/ingredients.api';
import MixerServiceFactory from './mixer/mixer.api';
import PremixServiceFactory from './premixes/premixes.api';
import RecipeServiceFactory from './recipes/recipes.api';
import ReportsServiceFactory from './reports/reports.api';
import ScaleTypeServiceFactory from './scaleType/scaleType.api';
import SharingServiceFactory from './sharing/sharing.api';
import OperatorServiceFactory from './operator/operator.api';
import { config } from '../../config';
import GatewayServiceFactory from './gateway/gateway.api';
import PresetServiceFactory from './preset/preset.api';
import SettingsServiceFactory from './settings/settings.api';
import DataToolsServiceFactory from './dataTools/dataTools.api';
import ZonesServiceFactory from './zones/zones.api';
import SubscriptionServiceFactory from './subscription/subscription.api';
import StorageServiceFactory from './storage/storage.api';
import LotsServiceFactory from './lots/lots.api';
import RelationsServiceFactory from './relations/relations.api';
import LotTypesServiceFactory from './lotTypes/lotTypes.api';
import LotMarketTypesServiceFactory from './lotMarketTypes/lotMarketTypes.api';
import HMSServiceFactory from './hms/hms.api';
import RefusalsServiceFactory from './refusals/refusals.api';
import InventoryServiceFactory from './inventory/inventory.api';
import AutomatedReportsServiceFactory from './automatedReports/automatedReports.api';
import OrdersServiceFactory from './orders/orders.api';
import WeighbridgeService from './weighbridge/weighbridge.api';
import DashboardServiceFactory from './dashboard/dashboard.api';
import CostTypesServiceFactory from './costTypes/costTypes.api';
import { DriverPermissionsFactory } from './driverPermissions/driverPermissions.api';
import LocationServiceFactory from './locations/locations.api';
import BinControlServiceFactory from './binControl/binControl.api';

export interface APIManagerConfig {
  farmId?: number;
  TENANT_ID: string;
}

export interface APIFactoryParams {
  HTTPBase: AxiosInstance;
  config: APIManagerConfig;
}

type apiManagerParams = {
  HTTPProfileManagerURL: string;
  HTTPSubscriptionURL: string;
  HTTPBaseURL: string;
  HTTPDashboardURL: string;
  HTTPStorageURL: string;
};

export const ApiManager = (params: apiManagerParams) => {
  const { HTTPProfileManagerURL, HTTPSubscriptionURL, HTTPBaseURL, HTTPStorageURL, HTTPDashboardURL } = params;
  const profileManagerAPI = generateConf({ baseURL: HTTPProfileManagerURL });
  const subscriptionsAPI = generateConf({ baseURL: HTTPSubscriptionURL });
  const HTTP = generateConf({ baseURL: HTTPBaseURL });
  const storageAPI = generateConf({ baseURL: HTTPStorageURL });
  const dashboardAPI = generateConf({ baseURL: HTTPDashboardURL });

  return {
    getProfileInstance: (): AxiosInstance => profileManagerAPI,
    getSubscriptionInstance: (): AxiosInstance => subscriptionsAPI,
    setAuthToken(authTokens: AuthenticationToken): void {
      setAuth(config.TENANT_ID, authTokens, profileManagerAPI, subscriptionsAPI, HTTP, storageAPI, dashboardAPI);
    },
    setFarmId(id: string): void {
      config.farmId = id;
    },
    auth: AuthServiceFactory,
    automatedReports: AutomatedReportsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    farms: FarmsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    ingredients: IngredientServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    premixes: PremixServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    gateway: GatewayServiceFactory({
      HTTPTmr: HTTP,
    }),
    recipes: RecipeServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    groupTypes: GroupTypesServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    group: GroupServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    code: CodeServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    common: CommonServiceFactory({
      HTTPProfileManager: profileManagerAPI,
    }),
    company: CompanyServiceFactory({
      HTTPProfileManager: profileManagerAPI,
    }),
    operator: OperatorServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    preset: PresetServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    user: UserServiceFactory({
      HTTPProfileManager: profileManagerAPI,
      HTTPSubscription: subscriptionsAPI,
      applicationId: config.APP_ID,
    }),
    feeding: FeedingServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    mixer: MixerServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    scaleType: ScaleTypeServiceFactory({
      HTTPBase: HTTP,
    }),
    reports: ReportsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    sharing: SharingServiceFactory({
      HTTPProfileManager: profileManagerAPI,
    }),
    settings: SettingsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    dataTools: DataToolsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    zones: ZonesServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    subscription: SubscriptionServiceFactory({
      HTTPBase: subscriptionsAPI,
    }),
    storage: StorageServiceFactory({
      HTTPBase: storageAPI,
    }),
    lots: LotsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    relations: RelationsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    lotTypes: LotTypesServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    lotMarketTypes: LotMarketTypesServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    hms: HMSServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    refusals: RefusalsServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    inventory: InventoryServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    orders: OrdersServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    weighbridge: WeighbridgeService({
      HTTPBase: HTTP,
      config,
    }),
    costTypes: CostTypesServiceFactory({
      HTTPBase: HTTP,
      config,
    }),
    dashboard: DashboardServiceFactory({ HTTPBase: dashboardAPI }),
    driverPermissions: DriverPermissionsFactory({ HTTPBase: HTTP, config }),
    locations: LocationServiceFactory({ HTTPBase: HTTP, config }),
    binControl: BinControlServiceFactory({ HTTPBase: HTTP, config }),
  };
};
